import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, ButtonGroup, ButtonGroupProps, Menu, MenuItem } from '@mui/material';
import { useLocalPlaylist } from 'hooks/useLocalPlaylist';
import { PlaylistExportType, usePlaylistExport } from 'hooks/usePlaylistExport';
import { EventOccasion } from 'models/EventOccasion';
import { TrackRequest } from 'models/TrackRequest';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createDownloadFile } from 'utils/createDownloadFile';
import { ExportSeratoDialog } from '../dialog/ExportSeratoDialog';

export interface ExportPlaylistActionMenuProps extends ButtonGroupProps {
  playlist: TrackRequest[];
  event: EventOccasion;
  onExport?: () => void;
}

export function ExportPlaylistActionMenu({
  onExport,
  playlist,
  event,
  ...rest
}: ExportPlaylistActionMenuProps) {
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | Element>(null);
  const openMenu = Boolean(menuAnchorEl);
  const { createPlaylistByType, getDefaultPlaylistType, createAndSaveCrate } =
    usePlaylistExport(event);
  const { getLocalPlaylist } = useLocalPlaylist();
  const [openSeratoDialog, setOpenSeratoDialog] = useState(false);

  const handleOpenMenu = (ev: React.MouseEvent<Element>) => {
    ev.preventDefault();
    setMenuAnchorEl(ev.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenSeratoDialog = () => {
    setOpenSeratoDialog(true);
  };

  const handleCloseSeratoDialog = () => {
    setOpenSeratoDialog(false);
  };

  const handleExportSeratoDialog = async () => {
    const localPlaylist = await getLocalPlaylist(playlist);
    await createAndSaveCrate(localPlaylist);
    setOpenSeratoDialog(false);
  };

  const handleExportPlaylist = async (type: PlaylistExportType) => {
    if (type === PlaylistExportType.CRATE) {
      handleOpenSeratoDialog();
    } else {
      const localPlaylist = await getLocalPlaylist(playlist);

      if (localPlaylist.length === 0) {
        return;
      }

      const content = await createPlaylistByType(type, localPlaylist);

      if (content) {
        createDownloadFile(content, event.name, type);
      }
    }

    onExport?.();
    handleCloseMenu();
  };

  return (
    <>
      <ButtonGroup variant="outlined" sx={{ lineHeight: 'normal' }} {...rest}>
        <Button
          sx={{ lineHeight: 'normal' }}
          onClick={() => handleExportPlaylist(getDefaultPlaylistType())}
        >
          {t('exportLength', 'Export ({{length}})', { length: playlist.length })}
        </Button>
        <Button
          aria-controls={openMenu ? 'split-button-menu' : undefined}
          aria-expanded={openMenu ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleOpenMenu}
          style={{ minWidth: '1.5rem' }}
          sx={{ px: 0 }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </ButtonGroup>
      <Menu anchorEl={menuAnchorEl} open={openMenu} onClose={handleCloseMenu}>
        {Object.values(PlaylistExportType).map((type) => (
          <MenuItem key={type} onClick={() => handleExportPlaylist(type)}>
            {t(`exportPlaylist${type}`, type)}
          </MenuItem>
        ))}
      </Menu>
      <ExportSeratoDialog
        event={event}
        tracks={playlist}
        open={openSeratoDialog}
        onExport={handleExportSeratoDialog}
        onClose={handleCloseSeratoDialog}
      />
    </>
  );
}
